import React, { createContext, useContext, useState, useEffect } from 'react';
import firebase from 'firebase';
import CONSTANTS from '../constants';
import AuthService, { UserRole } from '../services/AuthService';
import StripeConnectService from '../services/StripeConnectService';
import SubscriptionService, { SubscriptionStatus } from '../services/SubscriptionService';
import { Config } from '../config';

// Re-export UserRole for consumers of AuthContext
export type { UserRole };

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  roles: UserRole[];
  employeeId?: string;
  companyId?: string;
  firstName?: string;
  lastName?: string;
  getIdToken?: () => Promise<string>;
  stripeConnectStatus?: {
    isConnected: boolean;
    accountId?: string;
    detailsSubmitted?: boolean;
    chargesEnabled?: boolean;
    payoutsEnabled?: boolean;
  };
  subscription?: SubscriptionStatus;
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  userLoading: boolean;
  login: (email: string, password: string) => Promise<firebase.auth.UserCredential>;
  logout: () => Promise<void>;
  register: (email: string, password: string, displayName: string, roles?: UserRole[], employeeId?: string) => Promise<firebase.auth.UserCredential>;
  resetPassword: (email: string) => Promise<void>;
  updateUserRoles: (uid: string, roles: UserRole[], employeeId?: string) => Promise<void>;
  updateEmail: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;
  reauthenticate: (password: string) => Promise<firebase.auth.UserCredential>;
  verifyTokenAndNavigate: () => Promise<User>;
  refreshStripeConnectStatus: () => Promise<void>;
  refreshSubscriptionStatus: () => Promise<void>;
}

// Create context with default values
export const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  userLoading: true,
  login: () => Promise.resolve({} as firebase.auth.UserCredential),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve({} as firebase.auth.UserCredential),
  resetPassword: () => Promise.resolve(),
  updateUserRoles: () => Promise.resolve(),
  updateEmail: () => Promise.resolve(),
  updatePassword: () => Promise.resolve(),
  updateDisplayName: () => Promise.resolve(),
  verifyTokenAndNavigate: () => Promise.resolve({} as User),
  reauthenticate: () => Promise.resolve({} as firebase.auth.UserCredential),
  refreshStripeConnectStatus: () => Promise.resolve(),
  refreshSubscriptionStatus: () => Promise.resolve(),
});

// Custom hook to use auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);

  // Initialize Firebase if not already initialized
  useEffect(() => {
    if (firebase.apps.length === 0) {
      if (Config.isStaging()) {
        firebase.initializeApp(CONSTANTS.FIREBASE_STAGING);
      } else {
        firebase.initializeApp(CONSTANTS.FIREBASE);
      }
    }
  }, []);

  // Login function
  const login = (email: string, password: string) => {
    return firebase.auth().signInWithEmailAndPassword(email, password);
  };

  // Logout function
  const logout = () => {
    return firebase.auth().signOut();
  };

  // Register function
  const register = async (email: string, password: string, displayName: string, roles?: UserRole[], employeeId?: string) => {
    try {
      // Use backend service for registration
      await AuthService.register({
        email,
        password,
        displayName,
        roles: roles || ['admin'],
        employeeId
      });

      // After successful registration, sign in the user
      const result = await firebase.auth().signInWithEmailAndPassword(email, password);
      return result;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  // Update user roles function
  const updateUserRoles = async (uid: string, roles: UserRole[], employeeId?: string) => {
    await AuthService.updateUserRoles(uid, roles, employeeId);
  };

  // Reset password function
  const resetPassword = async (email: string) => {
    await AuthService.resetPassword(email);
  };

  // Reauthenticate user (required for sensitive operations)
  const reauthenticate = async (password: string) => {
    if (!firebase.auth().currentUser || !firebase.auth().currentUser?.email) {
      throw new Error('No user is currently logged in');
    }

    const credential = firebase.auth.EmailAuthProvider.credential(
      firebase.auth().currentUser?.email || (() => { throw new Error('User email is null'); })(),
      password
    );

    const result = await firebase.auth().currentUser?.reauthenticateWithCredential(credential);
    if (!result) {
      throw new Error('Reauthentication failed');
    }
    return result;
  };

  // Update user's email
  const updateEmail = async (email: string) => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error('No user is currently logged in');
    }

    await AuthService.updateEmail(currentUser.uid, email);
  };

  // Update user's password
  const updatePassword = async (password: string) => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error('No user is currently logged in');
    }

    await AuthService.updatePassword(currentUser.uid, password);
  };

  // Update user's display name
  const updateDisplayName = async (displayName: string) => {
    const firebaseUser = firebase.auth().currentUser;
    if (!firebaseUser) {
      throw new Error('No user is currently logged in');
    }

    await AuthService.updateDisplayName(firebaseUser.uid, displayName);

    // Update local user state
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        displayName: displayName
      });
    }
  };

  // Verify token via backend and update user state
  const verifyTokenAndNavigate = async (): Promise<User> => {
    try {
      console.log('Verifying token via backend...');
      const verifiedUser = await AuthService.verifyToken();

      // Update current user state with verified data
      setCurrentUser(verifiedUser);

      console.log('Token verified successfully, user data updated:', verifiedUser);

      // The navigation will be handled by the login page's useEffect
      // since it listens to currentUser changes

      return verifiedUser;
    } catch (error) {
      console.error('Token verification failed:', error);
      throw error;
    }
  };

  // Refresh Stripe Connect status
  const refreshStripeConnectStatus = async () => {
    if (!currentUser) return;

    try {
      const stripeStatus = await StripeConnectService.getStripeConnectStatus();
      setCurrentUser({
        ...currentUser,
        stripeConnectStatus: stripeStatus
      });
    } catch (error) {
      console.error('Error refreshing Stripe Connect status:', error);
    }
  };

  // Refresh Subscription status
  const refreshSubscriptionStatus = async () => {
    if (!currentUser) return;

    try {
      // Clear any cached subscription data
      sessionStorage.removeItem('subscriptionError');
      localStorage.removeItem('subscriptionCache');

      console.log('🔄 Refreshing subscription status for company:', currentUser.companyId);
      const subscriptionStatus = await SubscriptionService.getCurrentSubscription(currentUser.companyId);

      console.log('📊 New subscription status:', {
        hasActiveSubscription: subscriptionStatus.hasActiveSubscription,
        isInTrialPeriod: subscriptionStatus.isInTrialPeriod,
        trialDaysRemaining: subscriptionStatus.trialDaysRemaining,
        status: subscriptionStatus.status,
        accountCreatedAt: subscriptionStatus.accountCreatedAt
      });

      setCurrentUser({
        ...currentUser,
        subscription: subscriptionStatus
      });
    } catch (error) {
      console.error('Error refreshing subscription status:', error);
    }
  };

  // Set up auth state observer
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async user => {
      if (user) {
        // Create basic user info
        const userInfo: User = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          roles: [] // Initialize with empty roles array
        };

        try {
          setUserLoading(true);
          // Fetch additional user data from backend service
          const userData = await AuthService.getUserData(user.uid);

          // Add role information and additional fields
          userInfo.roles = userData.roles || ['admin'];
          userInfo.employeeId = userData.employeeId;
          userInfo.companyId = userData.companyId;
          userInfo.firstName = userData.firstName;
          userInfo.lastName = userData.lastName;
          userInfo.getIdToken = () => user.getIdToken();

          // Fetch Stripe Connect status if user has admin role
          if (userInfo.roles.includes('admin')) {
            try {
              const stripeStatus = await StripeConnectService.getStripeConnectStatus();
              userInfo.stripeConnectStatus = stripeStatus;
            } catch (error) {
              console.error("Error fetching Stripe Connect status:", error);
              // Set default status if fetch fails
              userInfo.stripeConnectStatus = { isConnected: false };
            }
          }

          // Fetch subscription status
          try {
            const subscriptionStatus = await SubscriptionService.getCurrentSubscription(userInfo.companyId);
            userInfo.subscription = subscriptionStatus;
          } catch (error) {
            console.error("Error fetching subscription status:", error);
            // Set default status if fetch fails
            userInfo.subscription = { hasActiveSubscription: false, status: 'none' };
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          // Default to admin if we can't fetch user data
          userInfo.roles = ['admin'];
        } finally {
          setCurrentUser(userInfo);
          setUserLoading(false);
          setLoading(false);
        }
      } else {
        setCurrentUser(null);
        setUserLoading(false);
        setLoading(false);
      }
    });

    // Cleanup subscription
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    userLoading,
    login,
    logout,
    register,
    resetPassword,
    updateUserRoles,
    updateEmail,
    updatePassword,
    updateDisplayName,
    reauthenticate,
    verifyTokenAndNavigate,
    refreshStripeConnectStatus,
    refreshSubscriptionStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};